/**
 * Airdrop Header Widget
 * Compact display for header showing airdrop status with 2-decimal SOL formatting
 */

import React, { useMemo } from 'react';
import { useAirdrop } from '../../contexts/AirdropContext';
import './AirdropHeaderWidget.css';

interface AirdropHeaderWidgetProps {
  onClick: () => void;
}

export function AirdropHeaderWidget({ onClick }: AirdropHeaderWidgetProps) {
  const { currentRound, loading } = useAirdrop();
  
  // Track seconds elapsed since data loaded
  const [secondsElapsed, setSecondsElapsed] = React.useState(0);
  
  /**
   * Calculate time remaining from server time
   */
  const timeRemaining = useMemo(() => {
    if (!currentRound || currentRound.timeRemainingSeconds === undefined) return null;
    
    // Server time minus seconds elapsed on client
    const remaining = Math.max(0, currentRound.timeRemainingSeconds - secondsElapsed);
    
    const hours = Math.floor(remaining / 3600);
    const minutes = Math.floor((remaining % 3600) / 60);
    
    // Show hours if > 0, otherwise just minutes (no seconds)
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}m`;
    }
    return `${minutes}m`;
  }, [currentRound, secondsElapsed]);
  
  /**
   * Update timer every second
   */
  React.useEffect(() => {
    if (!currentRound) return;
    
    // Reset elapsed time when new round data comes in
    setSecondsElapsed(0);
    
    const interval = setInterval(() => {
      setSecondsElapsed(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [currentRound]);
  
  // Don't show widget if no active round or data is missing
  if (!currentRound || loading.round || currentRound.totalPool === undefined || currentRound.timeRemainingSeconds === undefined) {
    return null;
  }
  
  const poolAmount = currentRound.totalPool?.toFixed(2) || '0.00'; // 2 decimals instead of 4
  
  return (
    <div className="airdrop-header-widget" onClick={onClick}>
      {/* Label on top */}
      <span className="airdrop-header-label">AIRDROP</span>
      
      {/* Bottom row: Amount and Timer */}
      <div className="airdrop-header-bottom">
        {/* Amount with SOL icon */}
        <div className="airdrop-header-amount-group">
          <img 
            src="/assets/icons/solanaLogoMark.svg" 
            alt="SOL" 
            className="airdrop-header-icon"
          />
          <span className="airdrop-header-amount">{poolAmount}</span>
        </div>
        
        {/* Separator */}
        <span className="airdrop-header-separator">|</span>
        
        {/* Timer */}
        <span className="airdrop-header-timer">{timeRemaining}</span>
      </div>
    </div>
  );
}

export default AirdropHeaderWidget;

